import { observer } from 'mobx-react-lite';
import React from 'react';
import { basedAxios } from '../../store/Store';
import EventLabel from '../../components/EventLabel/EventLabel';
import classname from 'classnames';

interface IAccredationListProps {
  orders: any[];
  ordersLoaded: boolean;
  questions: any[];
  breakouts?: any[];
  environment: {
    eventId: string;
    lang: string;
  } & unknown;
  settings: {
    pageAccessPersonType: any[];
    personDisplayQuestions: any[];
    personDisplayPersonTypes: any[];
    targetPath: string;
    confirmationPersonType: any;
  };
}

function Accreditation({ environment, settings, orders = [], breakouts = [], ordersLoaded = false, questions }: IAccredationListProps) {
  const [filterBy, setFilterBy] = React.useState('status');
  const [desc, setDesc] = React.useState(true);
  const ref = React.useRef(null);

  const isAxe = ['14805'].includes(environment?.eventId.toString()); // Check if the event is Axe

  const [attendeeList, setAttendeeList] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const { pageAccessPersonType = null, personDisplayQuestions = [], personDisplayPersonTypes = [], targetPath = '', confirmationPersonType } = settings;

  const isAuthorized = pageAccessPersonType ? orders?.[0]?.personTypes?.some((personType: any) => pageAccessPersonType === personType.personTypeId) : false;

  React.useEffect(() => {
    async function fetchOrders() {
      setIsLoading(true);
      try {
        const response = await basedAxios.get('./attendee/getAccreditationOrderByEvent/' + environment.eventId, {
          params: {
            personTypeIdsStr: personDisplayPersonTypes.join(','),
            questionOptionAnswerIdsStr: personDisplayQuestions.join(','),
          },
        });

        setAttendeeList(response.data);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    if (isAuthorized) {
      fetchOrders();
    }
  }, [isAuthorized]);

  const confirmedPersons = attendeeList?.filter((attendee: any) => attendee.personTypes?.includes(confirmationPersonType))?.length;

  const sortedAttendeeList = attendeeList?.sort((a, b) => {
    if (filterBy === 'name') {
      if (isAxe) {
        if (a.orderRows?.length > 0 && b.orderRows?.length > 0) {
          const aBreakoutName = breakouts?.find((breakout: any) => a?.orderRows?.includes(breakout.eventId))?.name ?? '-';
          const bBreakoutName = breakouts?.find((breakout: any) => b?.orderRows?.includes(breakout.eventId))?.name ?? '-';

          return desc ? aBreakoutName.localeCompare(bBreakoutName) : bBreakoutName.localeCompare(aBreakoutName);
        }
      }
      return desc ? a.firstName.localeCompare(b.firstName) : b.firstName.localeCompare(a.firstName);
    } else if (filterBy === 'status') {
      return desc
        ? a.personTypes.includes(confirmationPersonType) - b.personTypes.includes(confirmationPersonType)
        : b.personTypes.includes(confirmationPersonType) - a.personTypes.includes(confirmationPersonType);
    }
    return 0;
  });
  if (!ordersLoaded || !isAuthorized || !orders?.[0]) return null;

  return (
    <div className="w-full space-y-6 p-6">
      <div className="mb-4">
        {confirmedPersons > 0 && (
          <div className="flex gap-x-2">
            <p className="mt-2">
              <EventLabel name="ConfirmedPersons" groupName="CMS.Accreditation" /> {confirmedPersons}
            </p>{' '}
            /
            <p className="mt-2">
              <EventLabel name="NotConfirmedPersons" groupName="CMS.Accreditation" /> {attendeeList?.length - confirmedPersons}
            </p>
          </div>
        )}
      </div>

      {!isLoading ? (
        <table className="table-auto w-full max-h-[500px] overflow-y-auto divide-y rounded-md">
          <thead>
            <tr className="bg-gray-200">
              <th className="px-4 py-2">
                <EventLabel name="Name" groupName="CMS.Accreditation" />
                <button
                  className="bg-transparent border-none"
                  onClick={() => {
                    setFilterBy('name');
                    setDesc(!desc);
                  }}
                >
                  {filterBy === 'name' && desc ? <i className="ri-sort-asc" /> : <i className="ri-sort-desc" />}
                </button>
              </th>
              <th className="px-4 py-2">
                <EventLabel name="Status" groupName="CMS.Accreditation" />
                <button
                  className="bg-transparent border-none"
                  onClick={() => {
                    setFilterBy('status');
                    setDesc(!desc);
                  }}
                >
                  {filterBy === 'status' && desc ? <i className="ri-sort-asc" /> : <i className="ri-sort-desc" />}
                </button>
              </th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {sortedAttendeeList?.map((attendee: any) => (
              <tr key={attendee.badgeNo} className="border border-slate-100 bg-white">
                <td className="px-4 py-2 w-64 ">
                  <div className="flex gap-2 items-center">
                    {isAxe && (
                      <div className="w-14 font-bold">{breakouts?.find((breakout: any) => attendee?.orderRows?.includes(breakout.eventId))?.name ?? '-'}</div>
                    )}
                    <div>
                      {attendee.firstName} {attendee.lastName}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span
                    className={classname(
                      'rounded-full px-2 py-1 text-xs',
                      attendee.personTypes?.includes(confirmationPersonType) ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    )}
                  >
                    {attendee.personTypes?.includes(confirmationPersonType) ? (
                      <EventLabel name="Confirmed" groupName="CMS.Accreditation" />
                    ) : (
                      <EventLabel name="NotConfirmed" groupName="CMS.Accreditation" />
                    )}
                  </span>
                </td>
                <td className="px-4 py-2">{attendee.questionOptionAnswer}</td>
                <td className="px-4 py-2 text-right" align="right">
                  <a href={targetPath.toLowerCase()?.replace('[badgeno]', attendee.badgeNo)} target="_blank" rel="noreferrer">
                    <button className="!text-sm !w-fit !p-2 rounded-md cms-button">
                      <EventLabel name="Manage" groupName="CMS.Accreditation" />
                    </button>
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="flex flex-col divide-y divide-slate-200 w-full max-h-[500px] animate-pulse rounded-md">
          <div className="h-16 bg-slate-300" />
          <div className="h-16 bg-slate-300" />
          <div className="h-16 bg-slate-300" />
          <div className="h-16 bg-slate-300" />
          <div className="h-16 bg-slate-300" />
        </div>
      )}

      <div className="py-2 ">
        <EventLabel name="Quantity" groupName="CMS.Accreditation" /> {attendeeList?.length ?? 0}
      </div>
    </div>
  );
}

export default observer(Accreditation);
